import React, { useState } from "react";
import { Link } from "react-router-dom";
import DashboardCard from "./DashboardCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const PageCallToAction = () => {
  return (
    <div className="space-y-20">
      <section>
        <DashboardCard className="pt-8 pb-12 px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to get started?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Join thousands of developers and companies already using OpenPipe to
            build better AI applications.
          </p>
          <div className="flex flex-col sm:flex-row w-full max-w-md justify-center gap-4 mt-6 mx-auto">
            <a
              href="https://github.com/OpenPipe/ART"
              target="_blank"
              rel="noopener noreferrer"
              className="w-auto"
            >
              <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors w-auto">
                Use open source
              </button>
            </a>
            <Link to="/book-demo" className="w-auto">
              <button className="px-6 py-3 bg-secondary text-foreground rounded-lg font-medium hover:bg-secondary/80 transition-colors w-auto">
                Book a demo
              </button>
            </Link>
          </div>
        </DashboardCard>
      </section>

      <NewsletterCallToAction />
    </div>
  );
};

export const NewsletterCallToAction = () => {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleNewsletterSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from("newsletter_subscriptions")
        .insert({ email: newsletterEmail });

      if (error) {
        console.error("Newsletter subscription error:", error);

        toast({
          title:
            error.code === "23505"
              ? "Already Subscribed"
              : "Subscription Failed",
          description:
            error.code === "23505"
              ? "Great news! This email is already on our newsletter list. Thanks for your enthusiasm!"
              : "There was an error subscribing to the newsletter. Please try again.",
          variant: error.code === "23505" ? "default" : "destructive",
        });
        return;
      }

      setNewsletterEmail("");

      toast({
        title: "Subscription Successful",
        description: "Thank you for subscribing to our newsletter!",
        variant: "default",
      });

      console.log("Successfully subscribed!");
    } catch (err) {
      console.error("Unexpected error:", err);

      toast({
        title: "Subscription Failed",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section>
      <div className="text-center max-w-3xl mx-auto py-8">
        <h2 className="text-3xl font-bold mb-4">Join our newsletter</h2>
        <form
          onSubmit={handleNewsletterSubscription}
          className="max-w-md mx-auto flex space-x-2"
        >
          <Input
            type="email"
            placeholder="Enter your email"
            required
            value={newsletterEmail}
            onChange={(e) => setNewsletterEmail(e.target.value)}
            className="flex-1"
            disabled={isSubmitting}
          />
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Subscribing..." : "Subscribe"}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default PageCallToAction;
